import { Component, HostListener, OnInit } from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { ConfigSettingDto } from 'src/app/model/business/ConfigSettingDto';
import { IntermediaryDto } from '../../../model/business/IntermediaryDto';
import { CoverTypeEnum } from 'src/app/model/enums/CoverTypeEnum';
import { FontTypeEnum } from 'src/app/model/enums/FontTypeEnum';
import { OptionsEnum } from 'src/app/model/enums/OptionsEnum';
import { ConfigApiService } from 'src/app/services/ConfigApiService';
import { EnumConverterService } from 'src/app/services/EnumConverterService';
import { ComponentCanDeactivate } from 'src/app/services/PendingChangesGuard';
import { BaseOption } from '../../../model/BaseOption';
import { addMonths, startOfMonth } from 'date-fns';
import { formatDate } from '@angular/common';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { ProductTypeEnum } from '../../../model/enums/ProductTypeEnum';
import { IntermediaryService } from '../../../services/intermediaryService';
import { ValidationService } from '../../../services/ValidationService';
import {
  IntermediaryFeePeriodEnum,
  IntermediarySubscriptionFeePeriodEnum
} from '../../../model/enums/IntermediaryFeePeriodEnum';
import { getValueFromCurrencyInput } from '../../../utils/utils';

@Component({
  selector: 'app-configuration-orv-page',
  templateUrl: './configuration-page-orv.component.html',
  styleUrls: ['../configuration-page.common.css'],
})
export class ConfigurationPageOrvComponent
  implements OnInit, ComponentCanDeactivate {
  defaultValidations: [{ type: string; message: any }];
  intermediaryId: string;
  intermediary: IntermediaryDto;
  formGroupChanges: FormGroup;
  formGroupConfig: FormGroup;
  snapshotParam: string;
  yesNoList = this.enumConverterService.toBaseList('OptionsEnum', OptionsEnum);
  coverTypeList = this.enumConverterService.toBaseList(
    'CoverTypeEnum',
    CoverTypeEnum
  );
  fontTypes = this.enumConverterService.toBaseList(
    'FontTypeEnum',
    FontTypeEnum
  );
  CoverTypeEnum = CoverTypeEnum;
  OptionsEnum = OptionsEnum;
  FontTypeEnum = FontTypeEnum;
  buttonDisabled: boolean;
  datesArray: BaseOption[] = [];
  locale: string;
  public snippetCode: string;
  intermediaryFeePeriodList = this.enumConverterService.toBaseList(
    'IntermediaryFeePeriodEnum', IntermediaryFeePeriodEnum
  );
  intermediarySubscriptionFeePeriodList = this.enumConverterService.toBaseList(
    'IntermediaryFeePeriodEnum', IntermediarySubscriptionFeePeriodEnum
  );
  showIntermediarySubscriptionFeeSubscription: Subscription;
  showIntermediaryFeeSubscription: Subscription;

  constructor(
    public dialog: MatDialog,
    private router: Router,
    private fb: FormBuilder,
    private enumConverterService: EnumConverterService,
    private configApiService: ConfigApiService,
    private translationService: TranslateService,
    private validationService: ValidationService,
    private intermediaryService: IntermediaryService,
  ) {
    this.snippetCode = '';
  }

  get intermediaryFeeControl(): FormControl {
    return this.formGroupChanges.get('intermediaryFee') as FormControl;
  }

  get urlControl(): FormControl {
    return this.formGroupChanges.get('url') as FormControl;
  }

  get intermediarySubscriptionFeeControl(): FormControl {
    return this.formGroupChanges.get('intermediarySubscriptionFee') as FormControl;
  }

  get showIntermediaryFeeControl(): FormControl {
    return this.formGroupChanges.get('showIntermediaryFee') as FormControl;
  }

  get showIntermediarySubscriptionFeeControl(): FormControl {
    return this.formGroupChanges.get('showIntermediarySubscriptionFee') as FormControl;
  }

  get intermediarySubscriptionFeePeriodControl(): FormControl {
    return this.formGroupChanges.get('intermediarySubscriptionFeePeriod') as FormControl;
  }

  get intermediaryFeePeriodControl(): FormControl {
    return this.formGroupChanges.get('intermediaryFeePeriod') as FormControl;
  }

  get finalStatementAdditionControl(): FormControl {
    return this.formGroupChanges.get('finalStatementAddition') as FormControl;
  }
  get isAnyErrorForFinalStatementAddition(): boolean {
    const control = this.finalStatementAdditionControl;
    return control.invalid && (control.dirty || control.touched);
  }

  ngOnInit(): void {
    this.intermediaryService.getIntermediary()
      .subscribe((intermediary: IntermediaryDto) => {
          this.intermediary = intermediary;
          this.intermediaryId = intermediary.organisationId;
          this.showConfigSettings();
      });

    this.buttonDisabled = true;
    this.formGroupConfig = this.fb.group({
      sumAssured: ['', Validators.required],
      dateOfBirth: ['', Validators.required],
      isSmoker: [OptionsEnum.No, Validators.required],
      typeOfCover: ['', Validators.required],
      dateOfCommencement: [''],
      numberOfYears: [''],
    });

    this.formGroupChanges = this.fb.group({
      iframeHeight: [''],
      iframeWidth: [''],
      fontSizeP: [''],
      fontSizeH1: [''],
      fontSizeH2: [''],
      fontType: [''],
      fontColor: [''],
      backgroundTextColor: [''],
      backgroundColor: [''],
      inputColor: [''],
      inputBorderColor: [''],
      closingText: [''],
      emailFormControl: ['', Validators.email],
      intermediaryFee: ['', Validators.required],
      intermediaryFeePeriod: ['', Validators.required],
      url: ['', this.validationService.urlValidator()],
      showIntermediaryFee: [''],
      intermediarySubscriptionFeeText: [''],
      showIntermediarySubscriptionFee: [''],
      intermediarySubscriptionFeePeriod: ['', Validators.required],
      intermediarySubscriptionFee: ['', Validators.required],
      finalStatementAddition: ['', this.validationService.maxTextValidator()],
    });

    this.setupChangesEvents();

    this.locale = this.translationService.currentLang || this.translationService.defaultLang;
    this.translationService.onDefaultLangChange.subscribe((e: LangChangeEvent) => {
      this.locale = e.lang;
    });

    for (let i = 1; i < 4; i++) {
      const date = startOfMonth(addMonths(new Date(), i));
      this.datesArray.push({
        text: formatDate(date, 'longDate', this.locale),
        value: date,
      });
    }

    this.showIntermediaryFeeSubscription = this.formGroupChanges.get('showIntermediaryFee').valueChanges.pipe().subscribe(data => {
      const feeControl = this.formGroupChanges.get('intermediaryFee');
      const feePeriodControl = this.formGroupChanges.get('intermediaryFeePeriod');
      if (data) {
        feeControl.setValidators(Validators.required);
        feePeriodControl.setValidators(Validators.required);
      } else {
        feeControl.setValidators(null);
        feePeriodControl.setValidators(null);
      }
      feeControl.updateValueAndValidity();
      feePeriodControl.updateValueAndValidity();
    })

    this.showIntermediarySubscriptionFeeSubscription = this.formGroupChanges.get('showIntermediarySubscriptionFee').valueChanges.pipe().subscribe(data => {
      const feeControl = this.formGroupChanges.get('intermediarySubscriptionFee');
      const feePeriodControl = this.formGroupChanges.get('intermediarySubscriptionFeePeriod');
      if (data) {
        feeControl.setValidators(Validators.required);
        feePeriodControl.setValidators(Validators.required);
      } else {
        feeControl.setValidators(null);
        feePeriodControl.setValidators(null);
      }
      feeControl.updateValueAndValidity();
      feePeriodControl.updateValueAndValidity();
    })
  }

  ngOnDestroy(): void {
    this.showIntermediarySubscriptionFeeSubscription.unsubscribe();
    this.showIntermediaryFeeSubscription.unsubscribe();
  }

  setupChangesEvents(): void {
    this.formGroupChanges
      .valueChanges.subscribe(() => {
          this.changeInput();
      });
  }

  showConfigSettings(): void {
    this.configApiService
      .getConfigSettings(this.intermediaryId, ProductTypeEnum.ORV)
      .subscribe((data: ConfigSettingDto) => {
        this.updateFormGroup(data);
      });
  }

  updateFormGroup(configSetting: ConfigSettingDto): void {
    this.formGroupChanges
      .get('iframeHeight')
      .patchValue(configSetting.iframeHeight);
    this.formGroupChanges
      .get('iframeWidth')
      .patchValue(configSetting.iframeWidth);
    this.formGroupChanges
      .get('fontSizeP')
      .patchValue(configSetting.fontSizeParagraph);
    this.formGroupChanges
      .get('fontSizeH1')
      .patchValue(configSetting.fontSizeH1);
    this.formGroupChanges
      .get('fontSizeH2')
      .patchValue(configSetting.fontSizeH2);
    this.formGroupChanges.get('fontType').patchValue(configSetting.fontType);
    this.formGroupChanges.get('fontColor').patchValue(configSetting.fontColor);
    this.formGroupChanges
      .get('backgroundTextColor')
      .patchValue(configSetting.backgroundTextColor);
    this.formGroupChanges
      .get('backgroundColor')
      .patchValue(configSetting.backgroundColor);
    this.formGroupChanges
      .get('inputColor')
      .patchValue(configSetting.inputColor);
    this.formGroupChanges
      .get('inputBorderColor')
      .patchValue(configSetting.inputBorderColor);
    this.formGroupChanges
      .get('closingText')
      .patchValue(configSetting.closingText);
    this.formGroupChanges
      .get('emailFormControl')
      .patchValue(configSetting.intermediaryEmail);
    this.formGroupChanges
      .get('intermediaryFee')
      .patchValue(configSetting.intermediaryFee);
    this.formGroupChanges
      .get('url')
      .patchValue(configSetting.url);
    this.formGroupChanges
      .get('intermediaryFeePeriod')
      .patchValue(configSetting.intermediaryFeePeriod);
    this.formGroupChanges
      .get('showIntermediaryFee')
      .patchValue(configSetting.showIntermediaryFee);
    this.formGroupChanges
      .get('intermediarySubscriptionFeeText')
      .patchValue(configSetting.intermediarySubscriptionFeeText || '');
    this.formGroupChanges
      .get('showIntermediarySubscriptionFee')
      .patchValue(configSetting.showIntermediarySubscriptionFee);
    this.formGroupChanges
      .get('intermediarySubscriptionFeePeriod')
      .patchValue(configSetting.intermediarySubscriptionFeePeriod);
    this.formGroupChanges
      .get('intermediarySubscriptionFee')
      .patchValue(configSetting.intermediarySubscriptionFee || '');
    this.formGroupChanges
      .get('finalStatementAddition')
      .patchValue(configSetting.finalStatementAddition || '');
  }

  changeInput(): void {
    this.buttonDisabled = false;
  }

  saveConfigSettings(): void {
    this.formGroupChanges.markAllAsTouched();
    if (this.formGroupChanges.valid) {
      const configSettingDto = new ConfigSettingDto();
      configSettingDto.iframeHeight = this.formGroupChanges.value.iframeHeight;
      configSettingDto.iframeWidth = this.formGroupChanges.value.iframeWidth;
      configSettingDto.fontSizeParagraph = this.formGroupChanges.value.fontSizeP;
      configSettingDto.fontSizeH1 = this.formGroupChanges.value.fontSizeP * 2;
      configSettingDto.fontSizeH2 = this.formGroupChanges.value.fontSizeP * 1.5;
      configSettingDto.fontType = this.formGroupChanges.value.fontType;
      configSettingDto.fontColor = this.formGroupChanges.value.fontColor;
      configSettingDto.backgroundColor = this.formGroupChanges.value.backgroundColor;
      configSettingDto.backgroundTextColor = this.formGroupChanges.value.backgroundTextColor;
      configSettingDto.inputColor = this.formGroupChanges.value.inputColor;
      configSettingDto.inputBorderColor = this.formGroupChanges.value.inputBorderColor;
      configSettingDto.closingText = this.formGroupChanges.value.closingText;
      configSettingDto.intermediaryEmail = this.formGroupChanges.value.emailFormControl;
      configSettingDto.productType = ProductTypeEnum.ORV;
      configSettingDto.url = this.formGroupChanges.value.url;
      configSettingDto.showIntermediaryFee = this.formGroupChanges.value.showIntermediaryFee;
      configSettingDto.showIntermediarySubscriptionFee = this.formGroupChanges.value.showIntermediarySubscriptionFee;

      configSettingDto.intermediaryName = this.intermediary.organisationName;
      configSettingDto.intermediaryId = this.intermediary.organisationId;
      configSettingDto.intermediaryPhone = this.intermediary.telephone;

      if (this.formGroupChanges.value.showIntermediaryFee) {
        configSettingDto.intermediaryFee = getValueFromCurrencyInput(this.formGroupChanges.value.intermediaryFee);
        configSettingDto.intermediaryFeePeriod = this.formGroupChanges.value.intermediaryFeePeriod;
      }

      if (this.formGroupChanges.value.showIntermediarySubscriptionFee) {
        configSettingDto.intermediarySubscriptionFeeText = this.formGroupChanges.value.intermediarySubscriptionFeeText;
        configSettingDto.intermediarySubscriptionFeePeriod = this.formGroupChanges.value.intermediarySubscriptionFeePeriod;
        configSettingDto.intermediarySubscriptionFee = getValueFromCurrencyInput(this.formGroupChanges.value.intermediarySubscriptionFee);
      }
      configSettingDto.finalStatementAddition = this.formGroupChanges.value.finalStatementAddition;

      this.buttonDisabled = true;
      this.configApiService.saveConfigSettings(configSettingDto).subscribe();
    }
  }

  copy(snippetCode): void {
    snippetCode.select();
    document.execCommand('copy');
    snippetCode.setSelectRange(0, 0);
  }

  generateCode(): void {
    this.formGroupChanges.markAllAsTouched();
    if (this.formGroupChanges.valid) {
      this.saveConfigSettings();
      let configHref = '';
      const currentURL = window.location.href;
      if (currentURL.includes('/applicationrequester')) {
        configHref = currentURL.split('/#')[0];
      } else {
        configHref = window.location.origin;
      }
      this.snippetCode =
        '<iframe frameborder="0" src="' +
        configHref +
        '/#/orv/calculation/' +
        this.intermediaryId +
        '"' +
        ' style="height:' +
        this.formGroupChanges.value.iframeHeight +
        'px; ' +
        'width:100%; ' +
        'overflow-x:hidden; overflow-y:hidden;" title="TAF aanvraagtool"' +
        '></iframe>';
    } else {
      this.snippetCode = '';
    }
  }

  @HostListener('window:beforeunload')
  canDeactivate(): Observable<boolean> | boolean {
    return this.buttonDisabled;
  }
}
